/*
  Cart drawer side recommendations.

  Fills the panel docked to the left of the cart drawer. Ported from the doctorcip.eu
  implementation (Flawless theme) and adapted to Hyper.

  Responsibilities are deliberately narrow: fetch products and decide whether there is anything to
  show. Opening and closing is left to CSS, which keys off the drawer's own `[active]` attribute
  plus the `is-ready` class set here — so the panel cannot animate open while empty.

  Unlike the theme's in-drawer recommendation block, this element sits outside `#CartDrawerBody`
  and therefore survives the section swap on every cart update. It listens for `cart:updated`
  itself to re-key off the new first cart item and re-check the free-shipping threshold.
*/
class CartSideRecommendations extends HTMLElement {
  constructor() {
    super();
    this.isLoading = false;
    this.currentProductId = null;
    this.hasOpened = false;
    this.onCartUpdated = this.handleCartUpdate.bind(this);
  }

  connectedCallback() {
    // The drawer can be relocated to <body> when it opens, which re-runs this callback. Seed from
    // the rendered attributes only the first time, or a move would discard live cart state.
    if (!this.initialised) {
      this.cartTotal = Number(this.dataset.cartTotal) || 0;
      this.productId = this.dataset.productId || null;
      this.initialised = true;
    }
    document.addEventListener('cart:updated', this.onCartUpdated);

    // Fetch lazily on first open rather than on page load — the drawer renders on every page, and
    // most visits never open it. `drawer:handleAfterShow` only fires once the 0.6s slide has
    // finished, so watch the attribute instead and start the request as the drawer begins opening.
    this.drawer = this.closest('cart-drawer');
    if (!this.drawer) return;

    this.drawerObserver = new MutationObserver(() => this.handleDrawerToggle());
    this.drawerObserver.observe(this.drawer, { attributes: true, attributeFilter: ['open'] });

    // Two layouts, one element. Wide viewports get the panel beside the drawer; below 1025px there
    // is no room for it, so it moves inside the drawer body and docks to the bottom of the scroll
    // area as a carousel — the same two-mode behaviour as the reference implementation.
    if (!this.mediaQuery) {
      this.mediaQuery = window.matchMedia('(min-width: 1025px)');
      this.onLayoutChange = () => this.applyLayout();
      this.mediaQuery.addEventListener('change', this.onLayoutChange);
    }
    this.applyLayout();

    this.handleDrawerToggle();
  }

  disconnectedCallback() {
    document.removeEventListener('cart:updated', this.onCartUpdated);
    this.drawerObserver?.disconnect();
    // The media listener is deliberately left attached: moving between mounts disconnects and
    // reconnects this element, and tearing the listener down here would kill the very thing
    // driving the move.
  }

  // Move the element into whichever mount matches the current viewport. Re-parenting re-runs
  // connectedCallback, which is why the seeding above is guarded by `initialised`.
  applyLayout() {
    const wanted = this.mediaQuery.matches ? 'wide' : 'narrow';
    if (this.layout === wanted) return;

    const mount = this.drawer?.querySelector(`[data-cart-side-recs-mount="${wanted}"]`);
    if (!mount) return;

    this.layout = wanted;
    this.dataset.layout = wanted;
    if (this.parentElement !== mount) mount.appendChild(this);
  }

  get isDrawerOpen() {
    return this.drawer?.hasAttribute('open') ?? false;
  }

  handleDrawerToggle() {
    if (!this.isDrawerOpen) return;
    this.hasOpened = true;
    this.refresh();
  }

  // Parse "USD:100,CAD:120,JPY:500" → { USD: 100, ... }; returns null for a plain number.
  // Mirrors FreeShippingGoal in cart.js so both read the same theme setting the same way.
  parseConfig(raw) {
    const map = {};
    let hasMap = false;
    (raw || '').split(',').forEach((part) => {
      const [code, value] = part.split(':');
      if (value !== undefined) {
        const amount = parseFloat(String(value).replace(/[^0-9.]/g, ''));
        if (code && !isNaN(amount)) {
          map[code.trim().toUpperCase()] = amount;
          hasMap = true;
        }
      }
    });
    return hasMap ? map : null;
  }

  // Resolve the free shipping goal in the active currency's major units.
  resolveGoalMajor() {
    const config = this.dataset.freeShippingConfig;
    if (!config) return 0;

    const active = (this.dataset.activeCode || 'USD').toUpperCase();
    const shop = (this.dataset.shopCode || '').toUpperCase();
    const rate = Number((window.Shopify && window.Shopify.currency && window.Shopify.currency.rate) || 1);
    const map = this.parseConfig(config);

    if (map) {
      // Explicit value for the viewed currency — exact, no conversion.
      if (map[active] != null) return map[active];
      // Fallback: shop-currency value converted with the live rate.
      if (map[shop] != null) return map[shop] * rate;
      return 0;
    }

    // A single number is a shop-currency amount.
    const legacy = parseFloat(String(config).replace(/[^0-9.]/g, ''));
    return isNaN(legacy) ? 0 : legacy * rate;
  }

  // Blank config means the panel is not gated on free shipping and should always show.
  isBelowThreshold() {
    const goalMajor = this.resolveGoalMajor();
    if (goalMajor <= 0) return true;

    // Shopify money fields are always integer hundredths, regardless of the currency's real
    // decimal count, so always divide by 100.
    return this.cartTotal / 100 < goalMajor;
  }

  handleCartUpdate(event) {
    const cart = event?.detail?.cart;
    if (!cart) return;

    // Discounted total, matching how checkout applies the free-shipping threshold.
    this.cartTotal = Number(cart.total_price) || 0;
    // Recommendations key off a single product, so use the first line item — matching the
    // reference implementation.
    const first = cart.items && cart.items.length ? cart.items[0] : null;
    this.productId = first ? String(first.product_id) : null;

    // State is kept up to date either way, but don't spend a request until the drawer has been
    // opened at least once (a cart page edit, for instance, never opens it).
    if (this.hasOpened || this.isDrawerOpen) this.refresh();
  }

  close() {
    this.classList.remove('is-ready');
    this.setAttribute('aria-hidden', 'true');
  }

  open() {
    this.classList.add('is-ready');
    this.setAttribute('aria-hidden', 'false');
  }

  refresh() {
    if (!this.productId || !this.isBelowThreshold()) {
      this.close();
      return;
    }

    // Already showing recommendations for this product and still below the threshold — the cart
    // total changed but the pairings would not, so skip a redundant round trip.
    if (this.productId === this.currentProductId && this.list?.innerHTML.trim().length) {
      this.open();
      return;
    }

    this.fetchProducts();
  }

  fetchProducts() {
    if (this.isLoading) return;

    const productId = this.productId;
    const url = `${this.dataset.recommendationsUrl}?section_id=${this.dataset.sectionId}&product_id=${productId}&limit=${this.dataset.limit}&intent=${this.dataset.intent}`;

    this.isLoading = true;
    fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error('Network response was not ok');
        return response.text();
      })
      .then((responseText) => {
        const fetched = new DOMParser()
          .parseFromString(responseText, 'text/html')
          .querySelector('cart-side-recommendations [data-list]');

        // No pairings for this product — e.g. nothing set in Search & Discovery. Stay closed
        // rather than sliding out an empty panel.
        if (!fetched || !fetched.innerHTML.trim().length) {
          this.currentProductId = productId;
          this.close();
          return;
        }

        if (this.list) this.list.innerHTML = fetched.innerHTML;
        this.currentProductId = productId;

        // The cart may have changed while the request was in flight.
        if (this.productId === productId && this.isBelowThreshold()) {
          this.open();
        } else {
          this.close();
        }
      })
      .catch((error) => {
        console.error('Error loading cart side recommendations:', error);
        this.close();
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  get list() {
    return this.querySelector('[data-list]');
  }
}

customElements.get('cart-side-recommendations') ||
  customElements.define('cart-side-recommendations', CartSideRecommendations);
